/**
 * @tempest-limits file-lines — one pass that emits the whole token set — surfaces,
 * text, borders, states, the chart ramps — for both modes, with every value checked
 * against the surface it will sit on. The checks are what make the length: each
 * token is derived and then verified against the same background table.
 */
/**
 * Theme factory: turns a handful of brand colors into the full set of
 * `--tempest-*` token overrides, for both light and dark.
 *
 * Rebranding used to mean hand-writing ~167 custom properties (and getting the
 * dark ramp inversion right by hand). `createTheme({ primary: "#7c3aed" })`
 * emits the same thing, derived in OKLCH so the ramp stays perceptually even.
 */
import {
    contrastRatio,
    createColorScale,
    hexToRgbaString,
    readableForeground,
    type ColorScale,
    type ScaleStep,
} from "./color";
import { buildDivergingRamp, buildRamp, hueOf } from "./data-viz-ramps";
import { isDevBuild } from "../utils/dev-mode";

/** Radius presets, applied to the whole `--tempest-radius-*` family at once. */
export type ThemeRadius = "none" | "sm" | "md" | "lg" | "xl" | "full";

/** Status token families that {@link createTheme} can regenerate. */
export type ThemeStatus = "success" | "warning" | "danger" | "info";

/** Input for {@link createTheme}. Every field is optional — omitted families keep the built-in tokens. */
export interface CreateThemeOptions {
    /** Brand color, used as the `500` step of the primary scale. */
    primary?: string;
    /** Neutral color, used as the `500` step of the gray scale (surfaces, borders, text). */
    gray?: string;
    /** Success color (`--tempest-success*`). */
    success?: string;
    /** Warning color (`--tempest-warning*`). */
    warning?: string;
    /** Danger color (`--tempest-danger*`). */
    danger?: string;
    /** Info color (`--tempest-info*`). */
    info?: string;
    /**
     * Categorical series colors for `tempest-react-sdk/charts`, in cycle order.
     * Written to `--tempest-chart-1` … `--tempest-chart-N`, so charts follow the
     * theme instead of a hardcoded palette.
     */
    chart?: string[];
    /** Corner radius scale. A preset name, or explicit per-step values. */
    radius?: ThemeRadius | Partial<Record<"xs" | "sm" | "md" | "lg" | "xl" | "2xl", string>>;
    /**
     * Opacity of `--tempest-focus-ring-color`. Omit it — the default ring is opaque.
     *
     * A translucent ring has no contrast of its own; it has the contrast of
     * whatever it composites over. Passing a value below `1` reintroduces the
     * failure 0.61.0 fixed in the built-in tokens, and logs a warning in a
     * development build. Kept because a translucent ring over a background you
     * control is a legitimate choice.
     */
    focusRingAlpha?: number;
    /** Selector the light tokens are written under. Default `":root"`. */
    selector?: string;
    /** Selector the dark tokens are written under. Default `'[data-tempest-theme="dark"]'`. */
    darkSelector?: string;
}

/** A generated theme: token maps per color scheme, plus the CSS text that carries them. */
export interface GeneratedTheme {
    /** Light-scheme custom properties, without the leading `--`-less names (keys include `--`). */
    light: Record<string, string>;
    /** Dark-scheme custom properties. */
    dark: Record<string, string>;
    /** Both blocks rendered as CSS, ready for {@link applyTheme} or a stylesheet. */
    css: string;
}

const RADIUS_PRESETS: Record<ThemeRadius, Record<string, string>> = {
    none: { xs: "0", sm: "0", md: "0", lg: "0", xl: "0", "2xl": "0" },
    sm: { xs: "1px", sm: "2px", md: "4px", lg: "6px", xl: "8px", "2xl": "12px" },
    md: { xs: "2px", sm: "4px", md: "8px", lg: "12px", xl: "16px", "2xl": "24px" },
    lg: { xs: "4px", sm: "6px", md: "12px", lg: "16px", xl: "22px", "2xl": "32px" },
    xl: { xs: "6px", sm: "10px", md: "16px", lg: "24px", xl: "32px", "2xl": "44px" },
    full: { xs: "4px", sm: "8px", md: "9999px", lg: "9999px", xl: "9999px", "2xl": "9999px" },
};

const SCALE_STEPS: ScaleStep[] = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];

function writeScale(tokens: Record<string, string>, name: string, scale: ColorScale): void {
    for (const step of SCALE_STEPS) {
        tokens[`--tempest-${name}-${step}`] = scale[step];
    }
}

/** Minimum contrast for body text, WCAG 2.x AA. */
const AA_TEXT_CONTRAST = 4.5;

/**
 * Choose the ramp step for text sitting on the soft tint.
 *
 * A fixed step does not survive an arbitrary brand color: the built-in blue only
 * reaches 4.37:1 at `500` over its own `50` tint, and a generated emerald lands
 * at 4.41:1 at `600` — both fail AA for body text by a hair. So the step is
 * picked by measuring, walking away from the tint until it clears AA, and
 * falling back to the most extreme candidate when even that cannot (a very light
 * or very desaturated brand).
 *
 * @param scale - The generated ramp.
 * @param softStep - Step used as `--tempest-primary-soft`.
 * @param candidates - Steps to try, in order of preference.
 * @returns The first candidate clearing {@link AA_TEXT_CONTRAST}, else the last.
 */
function pickOnSoftStep(
    scale: ColorScale,
    softStep: ScaleStep,
    candidates: ScaleStep[],
): ScaleStep {
    for (const step of candidates) {
        if (contrastRatio(scale[softStep], scale[step]) >= AA_TEXT_CONTRAST) return step;
    }
    return candidates[candidates.length - 1];
}

/** Minimum contrast for a non-text indicator, WCAG 2.2 SC 1.4.11. */
const INDICATOR_CONTRAST = 3;

/**
 * The four surfaces a focus ring can land on, as the SDK's own `colors.css`
 * paints them.
 *
 * Ported from `src/styles/colors.css` (`--tempest-bg`, `--tempest-surface`,
 * `--tempest-surface-2`, `--tempest-surface-3`, resolved through the gray ramp),
 * and pinned by a test that reads that file — a theme that only names `primary`
 * keeps these, so the ring has to be measured against them and not against a
 * guess. The light values resolve through `--tempest-gray-50/100/200`; the dark
 * ones are literals in the dark block.
 */
const SDK_SURFACES: Record<"light" | "dark", readonly string[]> = {
    light: ["#ffffff", "#f8f9fb", "#f1f3f6", "#e4e7ec"],
    dark: ["#0b0d12", "#14171f", "#1d2230", "#262d3f"],
};

/**
 * Ramp steps to try for the focus ring, in order.
 *
 * `500` first, because the ring should read as the brand when it can, then up
 * the ramp — which moves away from the surfaces in **both** schemes, because
 * `createColorScale` inverts the dark ramp: there `50` is the darkest step (it
 * is what `--tempest-bg` is made of) and `900` the lightest. Walking down in
 * dark mode looks like the right inversion and is the wrong direction; measured,
 * it left a near-black brand's ring at 1.04:1 over its own background.
 */
const FOCUS_RING_CANDIDATES: readonly ScaleStep[] = [500, 600, 700, 800, 900];

/**
 * Choose the ramp step for the focus ring, by measuring it against the surfaces
 * it will actually be drawn on.
 *
 * A fixed step does not survive an arbitrary brand, and the measurement says so
 * loudly: of twelve brands checked against the four surfaces in both schemes —
 * 96 pairings — `500` opaque clears 3:1 in 58 of them, and `500` at the old
 * default alpha of 0.35 in 3. Picking the step by measurement clears all 96. The
 * brand from the report that opened this
 * (`#8100D7`) passes in light at 7.20:1 and fails in **dark** at 2.70:1 down to
 * 1.91:1 on `surface-3`; a yellow fails in light at 1.43:1. So the step is
 * picked the way {@link pickOnSoftStep} picks text on a tint: walk away from the
 * surfaces until the ring clears the floor against *all* of them.
 *
 * The fallback is the candidate with the best worst case rather than the last
 * one, because "the most extreme step" is not always the most contrasting one
 * once a brand is near-neutral.
 *
 * @param scale - The generated ramp.
 * @param surfaces - The four surfaces this theme will paint, opaque hex.
 * @returns The first candidate clearing {@link INDICATOR_CONTRAST} on every
 *   surface, else the candidate whose weakest pairing is strongest.
 */
function pickFocusRingStep(scale: ColorScale, surfaces: readonly string[]): ScaleStep {
    const worstCase = (step: ScaleStep): number =>
        Math.min(...surfaces.map((surface) => contrastRatio(scale[step], surface)));
    let best: ScaleStep = FOCUS_RING_CANDIDATES[0] as ScaleStep;
    let bestRatio = -1;
    for (const step of FOCUS_RING_CANDIDATES) {
        const measured = worstCase(step);
        if (measured >= INDICATOR_CONTRAST) return step;
        if (measured > bestRatio) {
            best = step;
            bestRatio = measured;
        }
    }
    return best;
}

/**
 * The surfaces this theme will actually paint, per scheme.
 *
 * A theme that names `gray` repaints all four, so measuring the ring against the
 * SDK's would be measuring the wrong backdrop — and a theme that does not keeps
 * the SDK's, which is the common case. The order and the ramp steps mirror
 * {@link writeNeutralAliases}, which is what emits them.
 *
 * @param scheme - Which scheme is being emitted.
 * @param grayScales - The generated neutral ramps, when the theme names one.
 * @returns Four opaque hex colors: bg, surface, surface-2, surface-3.
 */
function surfacesOf(
    scheme: "light" | "dark",
    grayScales: { light: ColorScale; dark: ColorScale } | undefined,
): readonly string[] {
    if (!grayScales) return SDK_SURFACES[scheme];
    const scale = grayScales[scheme];
    return scheme === "light"
        ? ["#ffffff", scale[50], scale[100], scale[200]]
        : [scale[50], scale[100], scale[200], scale[300]];
}

/**
 * Warn, in a development build, that an alpha was asked for the focus ring.
 *
 * A translucent ring has no contrast of its own — it has the contrast of
 * whatever it composites over — and that is the failure this option used to
 * ship by default: measured at alpha `0.35`, twelve brands across four surfaces
 * and both schemes produced 96 pairings, of which 3 cleared the 3:1 of WCAG 2.2
 * SC 1.4.11. It stays available, because a translucent ring over a
 * known background is a legitimate choice, but it is a choice a theme now has to
 * make on purpose.
 *
 * The warning is the difficult part to notice otherwise: a failing focus ring is
 * visible, it just does not separate.
 *
 * @param focusRingAlpha - The opacity the caller asked for, if any.
 */
function warnOnTranslucentFocusRing(focusRingAlpha: number | undefined): void {
    if (focusRingAlpha === undefined || focusRingAlpha >= 1) return;
    if (!isDevBuild()) return;
    console.warn(
        `[tempest-react-sdk] createTheme({ focusRingAlpha: ${focusRingAlpha} }) makes the focus ring ` +
            "translucent, so its contrast becomes whatever it composites over — measured below the 3:1 " +
            "of WCAG 2.2 SC 1.4.11 on every surface the SDK paints. Drop the option to get the opaque " +
            "ring, which is derived from your brand ramp and measured against those surfaces.",
    );
}

/**
 * The dark ink for content sitting on a saturated fill.
 *
 * Near-black tinted toward warm rather than pure black, matching the value
 * `colors.css` uses for the same job: it reads as part of the swatch instead of
 * a hole punched in it.
 */
const ON_SOLID_INK = "#1f0606";

/**
 * Emit the primary aliases for one scheme.
 *
 * The dark scheme walks the ramp the other way (hover is *lighter*, the soft
 * tint is a dark shade, and readable text on that tint is a light shade) — the
 * same inversion the built-in `colors.css` dark block does by hand.
 *
 * The selected-state indicator is derived from the same measurement as the focus
 * ring and emitted as its own token. It never takes `focusRingAlpha`: a
 * translucent ring over a known background is a legitimate choice a theme can
 * make, while a translucent *state* indicator has the contrast of whatever it
 * composites over, which is the defect this token exists to end.
 *
 * @param tokens - The token map for this scheme, written in place.
 * @param scale - The generated primary ramp.
 * @param scheme - Which scheme is being emitted.
 * @param surfaces - The surfaces this theme paints, for the focus-ring measurement.
 * @param focusRingAlpha - Opacity asked for the ring, or `undefined` for opaque.
 */
function writePrimaryAliases(
    tokens: Record<string, string>,
    scale: ColorScale,
    scheme: "light" | "dark",
    surfaces: readonly string[],
    focusRingAlpha: number | undefined,
): void {
    tokens["--tempest-primary"] = "var(--tempest-primary-500)";
    if (scheme === "light") {
        tokens["--tempest-primary-hover"] = "var(--tempest-primary-600)";
        tokens["--tempest-primary-active"] = "var(--tempest-primary-700)";
        tokens["--tempest-primary-soft"] = "var(--tempest-primary-50)";
        tokens["--tempest-primary-soft-hover"] = "var(--tempest-primary-100)";
        tokens["--tempest-primary-on-soft"] =
            `var(--tempest-primary-${pickOnSoftStep(scale, 50, [600, 700, 800, 900])})`;
    } else {
        tokens["--tempest-primary-hover"] = "var(--tempest-primary-400)";
        tokens["--tempest-primary-active"] = "var(--tempest-primary-300)";
        tokens["--tempest-primary-soft"] = "var(--tempest-primary-100)";
        tokens["--tempest-primary-soft-hover"] = "var(--tempest-primary-200)";
        tokens["--tempest-primary-on-soft"] =
            `var(--tempest-primary-${pickOnSoftStep(scale, 100, [700, 800, 900])})`;
    }

    const foreground = readableForeground(scale[500]);
    tokens["--tempest-primary-foreground"] = foreground;
    tokens["--tempest-text-on-primary"] = foreground;
    const ring = scale[pickFocusRingStep(scale, surfaces)];
    tokens["--tempest-focus-ring-color"] =
        focusRingAlpha === undefined || focusRingAlpha >= 1
            ? ring
            : hexToRgbaString(ring, focusRingAlpha);
    tokens["--tempest-selected-indicator"] = ring;
}

/**
 * Emit the neutral surface/border/text aliases from a generated gray scale.
 *
 * `--tempest-bg` is pushed past the ramp on purpose: pure white in light mode and
 * a shade darker than `gray-50` in dark mode, so a raised surface still reads as
 * raised against the page.
 */
function writeNeutralAliases(
    tokens: Record<string, string>,
    scale: ColorScale,
    scheme: "light" | "dark",
): void {
    if (scheme === "light") {
        tokens["--tempest-bg"] = "#ffffff";
        tokens["--tempest-surface"] = "var(--tempest-gray-50)";
        tokens["--tempest-surface-2"] = "var(--tempest-gray-100)";
        tokens["--tempest-surface-3"] = "var(--tempest-gray-200)";
        tokens["--tempest-border"] = "var(--tempest-gray-200)";
        tokens["--tempest-border-strong"] = "var(--tempest-gray-300)";
        tokens["--tempest-text"] = "var(--tempest-gray-900)";
        tokens["--tempest-text-muted"] = "var(--tempest-gray-600)";
        tokens["--tempest-text-subtle"] = "var(--tempest-gray-500)";
    } else {
        tokens["--tempest-bg"] = scale[50];
        tokens["--tempest-surface"] = "var(--tempest-gray-100)";
        tokens["--tempest-surface-2"] = "var(--tempest-gray-200)";
        tokens["--tempest-surface-3"] = "var(--tempest-gray-300)";
        tokens["--tempest-border"] = "var(--tempest-gray-300)";
        tokens["--tempest-border-strong"] = "var(--tempest-gray-400)";
        tokens["--tempest-text"] = "var(--tempest-gray-900)";
        tokens["--tempest-text-muted"] = "var(--tempest-gray-700)";
        tokens["--tempest-text-subtle"] = "var(--tempest-gray-600)";
    }
    tokens["--tempest-neutral-on-solid"] = readableForeground(scale[700], "#ffffff", ON_SOLID_INK);
}

/**
 * Emit one status family (`--tempest-danger`, `-fg`, `-bg`, `-border`, `-solid`).
 *
 * `-fg` is the text shade over `-bg`, so it has to cross the ramp in opposite
 * directions per scheme; `-solid` stays the saturated fill used by badges.
 *
 * `-on-solid` is derived from the `-solid` this call just emitted, rather than
 * left to fall through. Falling through was the bug: the SDK's own
 * `-on-solid` values are measured against the SDK's own fills, so a brand whose
 * `danger-600` lands light kept white ink over it and nothing said so. The
 * generated neutral hit exactly that — white over a generated `gray-700` of
 * `#a8b2c6` measures 2.13:1.
 */
function writeStatus(
    tokens: Record<string, string>,
    name: ThemeStatus,
    scale: ColorScale,
    scheme: "light" | "dark",
): void {
    if (scheme === "light") {
        tokens[`--tempest-${name}`] = scale[700];
        tokens[`--tempest-${name}-fg`] = scale[800];
        tokens[`--tempest-${name}-bg`] = scale[50];
        tokens[`--tempest-${name}-border`] = scale[200];
        tokens[`--tempest-${name}-solid`] = scale[600];
        tokens[`--tempest-${name}-on-solid`] = readableForeground(
            scale[600],
            "#ffffff",
            ON_SOLID_INK,
        );
    } else {
        tokens[`--tempest-${name}`] = scale[700];
        tokens[`--tempest-${name}-fg`] = scale[700];
        tokens[`--tempest-${name}-bg`] = scale[50];
        tokens[`--tempest-${name}-border`] = scale[200];
        tokens[`--tempest-${name}-solid`] = scale[500];
        tokens[`--tempest-${name}-on-solid`] = readableForeground(
            scale[500],
            "#ffffff",
            ON_SOLID_INK,
        );
    }
}

function renderBlock(selector: string, tokens: Record<string, string>): string {
    const entries = Object.entries(tokens);
    if (entries.length === 0) return "";
    const body = entries.map(([name, value]) => `    ${name}: ${value};`).join("\n");
    return `${selector} {\n${body}\n}`;
}

/**
 * Generate `--tempest-*` overrides from a small brand description.
 *
 * Only the families you pass are generated; everything else falls through to the
 * SDK's own tokens, so a theme stays a patch and not a fork of `colors.css`.
 *
 * @example
 * ```ts
 * import { applyTheme, createTheme } from "tempest-react-sdk";
 *
 * const theme = createTheme({
 *   primary: "#7c3aed",
 *   radius: "lg",
 *   chart: ["#7c3aed", "#0ea5e9", "#22c55e", "#f59e0b"],
 * });
 *
 * applyTheme(theme);
 * ```
 *
 * @param options - Brand colors plus optional radius / chart / focus-ring tuning.
 * @returns The light and dark token maps and the CSS text that carries them.
 */
export function createTheme(options: CreateThemeOptions = {}): GeneratedTheme {
    const {
        primary,
        gray,
        chart,
        radius,
        focusRingAlpha,
        selector = ":root",
        darkSelector = '[data-tempest-theme="dark"]',
    } = options;

    warnOnTranslucentFocusRing(focusRingAlpha);

    const light: Record<string, string> = {};
    const dark: Record<string, string> = {};

    // `anchor: false`: a neutral ramp has to keep its tuned lightness curve.
    // Anchoring it at the input compressed both halves and dropped
    // text-muted-on-surface-3 to ~4.2:1 — below AA — which the browser axe
    // sweep caught on the generated themes.
    const grayScales = gray
        ? {
              light: createColorScale(gray, "light", { anchor: false, neutral: true }),
              dark: createColorScale(gray, "dark", { anchor: false, neutral: true }),
          }
        : undefined;

    if (primary) {
        const lightScale = createColorScale(primary, "light");
        const darkScale = createColorScale(primary, "dark");
        writeScale(light, "primary", lightScale);
        writeScale(dark, "primary", darkScale);
        writePrimaryAliases(
            light,
            lightScale,
            "light",
            surfacesOf("light", grayScales),
            focusRingAlpha,
        );
        writePrimaryAliases(
            dark,
            darkScale,
            "dark",
            surfacesOf("dark", grayScales),
            focusRingAlpha,
        );
    }

    if (grayScales) {
        writeScale(light, "gray", grayScales.light);
        writeScale(dark, "gray", grayScales.dark);
        writeNeutralAliases(light, grayScales.light, "light");
        writeNeutralAliases(dark, grayScales.dark, "dark");
    }

    for (const status of ["success", "warning", "danger", "info"] as const) {
        const value = options[status];
        if (!value) continue;
        writeStatus(light, status, createColorScale(value, "light"), "light");
        writeStatus(dark, status, createColorScale(value, "dark"), "dark");
    }

    if (chart?.length) {
        chart.forEach((color, index) => {
            light[`--tempest-chart-${index + 1}`] = color;
        });
        // Declares how many series colors this theme owns. Without it the reader
        // keeps walking into the SDK's built-in `--tempest-chart-7`/`-8`, so a
        // 6-color brand palette silently mixes with two leftover defaults — which
        // is exactly what a 7-series chart would show.
        light["--tempest-chart-count"] = String(chart.length);
    }

    /*
     * Continuous scales follow the brand too.
     *
     * Without this a rebranded app gets its categorical slots recoloured and then a
     * heatmap still painted in the SDK's blue — the one chart on the page that
     * silently ignores the brand. Both scales derive from the first series hue (or
     * the primary), stepped by perceptual lightness for each mode separately.
     *
     * Skipped entirely when the theme names no colour to derive from, so the
     * built-in tokens survive instead of being overwritten with a guess.
     */
    const scaleSource = chart?.[0] ?? primary;
    if (scaleSource) {
        const coolHue = hueOf(scaleSource);
        // The opposite pole: the brand's own danger colour when it has one, since a
        // diverging scale's warm arm and "bad" should not disagree on screen.
        const warmHue = options.danger ? hueOf(options.danger) : (coolHue + 180) % 360;
        for (const [target, mode] of [
            [light, "light"],
            [dark, "dark"],
        ] as const) {
            buildRamp(coolHue, mode).forEach((stepColor, index) => {
                target[`--tempest-chart-sequential-${index + 1}`] = stepColor;
            });
            const diverging = buildDivergingRamp({
                coolHue,
                warmHue,
                mid: mode === "light" ? "#e4e7ec" : "#262d3f",
                mode,
            });
            [...diverging.cool, diverging.mid, ...diverging.warm].forEach((stepColor, index) => {
                target[`--tempest-chart-diverging-${index + 1}`] = stepColor;
            });
        }
    }

    if (radius) {
        const steps = typeof radius === "string" ? RADIUS_PRESETS[radius] : radius;
        for (const [step, value] of Object.entries(steps)) {
            light[`--tempest-radius-${step}`] = value;
        }
    }

    const css = [renderBlock(selector, light), renderBlock(darkSelector, dark)]
        .filter(Boolean)
        .join("\n\n");

    return { light, dark, css };
}

/**
 * Contrast ratio of `--tempest-primary-foreground` over the brand color.
 *
 * Exposed so an app (or a test) can assert its own brand clears WCAG AA (4.5) for
 * body text or AA-large (3.0) for button labels, instead of trusting the pick.
 *
 * @param options - The same input given to {@link createTheme}.
 * @returns The ratio, or `null` when no `primary` was provided.
 */
export function themeContrast(options: CreateThemeOptions): number | null {
    if (!options.primary) return null;
    const scale = createColorScale(options.primary, "light");
    return contrastRatio(scale[500], readableForeground(scale[500]));
}
